'use client';

import { useBunja } from 'bunja/react';
import { keyBy } from 'es-toolkit';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { Badge } from '@flipster/react/design-system/badge';
import { Styles, css } from '@flipster/styled-system/css';
import { center, grid, stack } from '@flipster/styled-system/patterns';

import { ImgOrLottie } from '../assets';
import { luckyDrawBunja } from './state';

interface Props {
  css?: Styles;
}

export const Rewards = ({ css: cssProps }: Props) => {
  const { luckyDrawsLayout, rewardsAtom } = useBunja(luckyDrawBunja);
  const rewards = useAtomValue(rewardsAtom);

  const prizesKeyById = useMemo(() => {
    if (luckyDrawsLayout == null) return {};
    return keyBy(
      luckyDrawsLayout.flatMap((l) => l.prizesCollection?.items),
      (i) => i?.id ?? 'unknown',
    );
  }, [luckyDrawsLayout]);

  if (!rewards.length) return null;
  return (
    <div
      className={css(
        stack.raw({
          gap: 10,
          p: 20,
          mt: 32,
          bg: 'background.default.level-1',
          rounded: 16,
        }),
        cssProps,
      )}
    >
      <p
        className={css({
          textStyle: 'Title/XS',
          color: 'content.default.level-5',
          textAlign: 'left',
        })}
      >
        Your rewards:
      </p>
      <ul
        className={grid({
          gridTemplateColumns: { base: 'repeat(2, 1fr)', tablet: 'repeat(auto-fill, minmax(156px, max-content))' },
          gap: 8,
        })}
      >
        {rewards.map((reward) => {
          const prize = prizesKeyById[reward.id];
          if (!prize) return null;
          return (
            <li className={center({ bg: 'background.default.level-3', px: 16, py: 8, rounded: 8 })} key={reward.id}>
              <ImgOrLottie url={prize.image?.url} alt={prize.name ?? 'reward'} css={{ w: 85, h: 85 }} />
              <Badge type='tint' color='promotion'>{`x${reward.count}`}</Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
